import * as fs from 'fs';
import * as path from 'path';
import { isMatrixDropdownVariant, type MatrixVariantRow } from './survey-inventory-extract';

export const LAYOUT_AUDIT_FILENAME = 'layout-audit.md';

const AUDIT_HEADER = `# Layout audit (HOOK 3 — blocking before preview)

> Fill every row after code audit vs \`implementation_plan.md\` §3c. **Broken must be 0** before \`npx cfm-sdk verify-layout\` passes.
> Template: [\`reverification-hooks.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/reverification-hooks.md) §HOOK 3 audit report.

## Verification summary

| Status | Count | Meaning |
| Broken | 0 | Must be 0 before preview |
| Needs improvement | 0 | P2 polish — non-blocking |
| OK | 0 | Code matches §3c variant |

## Matrix format (per §3c row)

| questionId | type | displayStyle | labelIndexMap | nonNaM | naTrailing | carouselParity | uxShell | Verdict | Doc anchor |
|------------|------|--------------|---------------|--------|------------|----------------|---------|---------|------------|
`;

function auditRowCells(row: MatrixVariantRow): string {
  return `| ${row.questionId} | ${row.type} | ${row.displayStyle} |  |  |  |  |  |  | layout-cookbook |`;
}

function smokeTestHint(row: MatrixVariantRow): string {
  if (isMatrixDropdownVariant(row)) {
    return 'No scale header row — one MatrixDropdown per statement; labels only inside dropdown options';
  }
  if (row.type === 'SLIDER_MATRIX') return 'Tick centers on thumb';
  if (/numbered/i.test(row.displayStyle)) return 'One number row; badge not label text';
  if (row.type === 'CFM_MATRIX' && /carousel/i.test(row.gridLayout)) {
    return 'Statement once in card; label i + radio i per column; chevrons + dots';
  }
  if (row.type === 'CFM_MATRIX' && /bipolar/i.test(row.statementLayout)) {
    const nonNa = parseInt(row.nonNaM, 10);
    const isSlider =
      /SLIDER/i.test(row.matrixFormat) || (row.matrixFormat === '—' && Number.isFinite(nonNa) && nonNa > 7);
    if (isSlider) {
      return 'Slider track visible in center zone (not 10 radios)';
    }
    return 'Left | scale | right visible';
  }
  if (/star|emoji/i.test(row.displayStyle)) return 'Each column aligns under anchor';
  if (row.type === 'CFM_MATRIX' || /likert/i.test(row.displayStyle)) {
    return 'All m options visible unselected';
  }
  return 'Matrix cells align under headers';
}

function visualSmokeRow(row: MatrixVariantRow): string {
  return `| ${row.questionId} | ${smokeTestHint(row)} |  |`;
}

const VISUAL_SMOKE_HEADER = `
## Visual smoke (agent handoff — fill before Phase 5b)

| questionId | Smoke test | Pass |
|------------|------------|------|
`;

const PITFALL_LEDGER_HEADER = `
## Agent pitfall ledger — matrix dropdown header row

> Read before HOOK 3 when §3c has matrix dropdown/selectbox. Grid header rules do **not** apply.

| Type | Dropdown trigger | Header in dropdown mode? |
|------|------------------|--------------------------|
| CSAT_MATRIX | \`displayStyle\` selectbox/dropdown or \`gridLayout\` dropdown | NO |
| RATING_MATRIX | same | NO |
| CFM_MATRIX | \`gridLayout\` dropdown only | NO |

**Symptom:** Scale labels (Strongly Disagree, Neutral, Agree, …) render as a clumped header row above MatrixDropdown rows.

**Cause:** Grid header helpers (\`renderHeaderRow\`, \`scaleAnchorLabels\`, anchor/column header band) called inside dropdown branch. Those belong to grid modes only.

| # | Gap | Fix |
|---|-----|-----|
| 1 | \`05-csat-matrix-scale.md\` says "Every grid MUST show exactly one semantic header row" without dropdown exception first | Header rule applies only in grid layout — not \`CsatMatrixVerticalList\` / \`LikertMatrixDropdownLayout\` |
| 2 | \`layout-tasks.md\` appends "Header: one row…" to dropdown §3c rows | Skip header task when matrix dropdown variant |
| 3 | Shared \`renderHeaderRow()\` copied into dropdown branch | Dropdown branch must early-return with no header helper — see \`LikertMatrixDropdownLayout\` |
| 4 | Checklist "Column headers OR scaleAnchorLabels visible" reads global | Grid-only; MatrixDropdown per row → headers redundant |
| 5 | \`verify-layout\` has no grep for header inside dropdown layout function | Run mandatory grep below for every matrix dropdown §3c row |

**Correct structure:** statement text → \`MatrixDropdown\` per row. Do **not** render \`scaleColumns[].label\` header, anchor row, or flex justify-between label strip.

**Mandatory grep (not fully covered by verify-layout):**

\`\`\`bash
# CSAT/RATING
grep -n "selectbox\\|displayStyle === 'dropdown'\\|gridLayout === 'dropdown'" src/components/CsatMatrixScale.tsx
grep "MatrixDropdown" src/components/CsatMatrixScale.tsx
# FAIL if scaleAnchorLabels / renderHeaderRow inside CsatMatrixVerticalList
# CFM
grep -n "gridLayout === 'dropdown'" src/components/LikertMatrixScale.tsx
grep "LikertMatrixDropdownLayout\\|MatrixDropdown" src/components/LikertMatrixScale.tsx
# FAIL if likert header helpers inside LikertMatrixDropdownLayout
\`\`\`

**Docs:** [\`field-binding-map.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/field-binding-map.md) · [\`csat_matrix.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/csat_matrix.md) · [\`cfm_matrix.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/cfm_matrix.md)
`;

const BIPOLAR_SLIDER_PITFALL_LEDGER = `
## Agent pitfall ledger — CFM bipolar SLIDER (two layers, two divisors)

> Read before HOOK 3 when §3c has CFM bipolar + \`matrixFormat=SLIDER\` (or \`nonNaM > 7\`). Branch in \`LikertMatrixScale\` — not \`SliderMatrixScale\`.

**Symptom:** Thumb snaps to ~2 positions; does not align with 10 discrete scale columns. Anchor labels cluster on the left while thumb lands at wrong %.

**Cause:** Two horizontal math layers conflated — anchors use \`m\` or bespoke \`100/(2*numCols)\`; thumb uses \`ticks={2}\` or divisor \`n\` instead of \`m\`.

| Layer | Divisor | Formula |
|-------|---------|---------|
| Header anchors (Label 1/2/3) | \`n = scaleAnchorLabels.length\` | \`columnPercent(i, n)\` |
| Slider thumb | \`m = nonNaCols.length\` | \`columnPercent(idx, m)\` via \`positionMode="discrete"\` + \`ticks={m}\` |

| # | Gap | Why it causes the bug | Mitigation |
|---|-----|----------------------|------------|
| 1 | \`trackOnly\` + \`hideTooltip\` misread | Agents set \`tickLabels={['','']}\` and \`ticks={2}\` thinking both are suppressed | \`ticks\` sets thumb snap count — keep \`ticks={m}\`; use \`hideTooltip\`/\`trackOnly\` to hide labels only |
| 2 | Copy-paste from graphics track | RATING graphics uses \`ticks={scaleColumns.length}\` — not \`ticks={2}\`; bipolar SLIDER is not graphics | Use \`ticks={nonNaCols.length}\` in \`LikertMatrixScale\` |
| 3 | \`cfm_matrix.md\` CORRECT block ignored | Lines show \`ticks={nonNaCols.length}\` explicitly | Read full TSX block — not just "use CustomSliderTrack" |
| 4 | \`layout-tasks.md\` under-specifies | Said "CustomSliderTrack in center (discrete)" without \`ticks={m}\` | Cross-check \`cfm_matrix.md\` for full wiring |
| 5 | SLIDER_MATRIX vs bipolar SLIDER conflation | SLIDER_MATRIX = continuous + \`tickPercent\`; bipolar SLIDER = discrete + \`columnPercent\` | Branch on \`matrixFormat === 'SLIDER'\` in \`LikertMatrixScale\` |
| 6 | \`verify-layout\` blind spot | Checks \`CustomSliderTrack\` + \`isBipolarSlider\` but not \`ticks={2}\` vs \`ticks={nonNaCols.length}\` | Run mandatory grep below for every §3c \`matrixFormat=SLIDER\` row |

**Mandatory grep (not fully covered by verify-layout):**

\`\`\`bash
grep -n "ticks={2}" src/components/LikertMatrixScale.tsx && echo BROKEN
grep -n "ticks={.*nonNaCols\\|ticks={nonNaM" src/components/LikertMatrixScale.tsx
grep "columnPercent" src/components/LikertMatrixScale.tsx
grep -E 'CustomSliderTrack|isBipolarSlider|positionMode="discrete"' src/components/LikertMatrixScale.tsx
\`\`\`

**Docs:** [\`scale-position-math.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/scale-position-math.md) §Bipolar SLIDER · [\`cfm_matrix.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/cfm_matrix.md) · [\`custom-slider-track.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/03-ui-specs/shared/custom-slider-track.md)
`;

function isBipolarSliderVariant(row: MatrixVariantRow): boolean {
  if (row.type !== 'CFM_MATRIX' || !/bipolar/i.test(row.statementLayout)) return false;
  const nonNa = parseInt(row.nonNaM, 10);
  return (
    /SLIDER/i.test(row.matrixFormat) ||
    (row.matrixFormat === '—' && Number.isFinite(nonNa) && nonNa > 7) ||
    /matrixFormat=SLIDER/i.test(row.branchOn ?? '')
  );
}

const RATING_GRAPHICS_PITFALL_LEDGER = `
## Agent pitfall ledger — RATING graphics slider

> Read before HOOK 3 when §3c has \`RATING_MATRIX\` + \`displayStyle=graphics\`. Branch in \`CsatMatrixScale\` — not \`SliderMatrixScale\`, not bipolar SLIDER.

**Symptom A:** Thumb between labels; only ~2 interactive positions.

**Symptom B:** Labels align with thumb, but gray track extends past thumb at min/max.

**Cause:** \`ticks={2}\` instead of \`ticks={m}\`; \`columnPercent\` on full-width track without \`GraphicsScaleZone\` inset.

| Layer | Position inside zone | Formula |
|-------|---------------------|---------|
| Column labels | Linear 0% → 100% of inner width | \`graphicsLabelPercent(i, m)\` |
| Thumb + fill | Index 0 → m-1 | \`positionMode="continuous"\` → \`tickPercent(value, 0, m-1)\` |
| Zone padding | Half-column inset each side | \`graphicsScaleInsetPercent(m)\` |

| # | Gap | Why agents fail | Mitigation |
|---|-----|-----------------|------------|
| 1 | \`ticks={2}\` copy-paste | \`trackOnly\`/\`hideTooltip\` misread — agents think ticks only affects visible labels | \`ticks={scaleColumns.length}\` always |
| 2 | \`trackOnly\` misread | Labels come from \`GraphicsColumnLabelsRow\`, not \`ticks\` | \`ticks\` = thumb snap count + \`getEmojiForIndex\` |
| 3 | Scale-zone inset buried | \`rating_matrix.md\` mentioned inset but CORRECT block lacked \`GraphicsScaleZone\` | Shared wrapper on labels + tracks |
| 4 | \`columnPercent\` passes grep, fails visually | Label and thumb both at 7.14% but track runs 0–100% | Runtime smoke: no gray beyond thumb at min/max |
| 5 | \`verify-layout\` blind spot | Checks \`columnPercent\` + \`getEmojiForIndex\`; not \`ticks={m}\` or zone | Mandatory grep below |
| 6 | Bipolar SLIDER conflation | Bipolar uses discrete \`columnPercent\` on full center zone | Graphics needs \`GraphicsScaleZone\` — different context |
| 7 | Doc said \`positionMode="discrete"\` on full width | Works for label↔thumb parity; not track-end parity | Inside zone: \`positionMode="continuous"\` |
| 8 | Per-row labels (Q22) | Duplicating labels inside \`statementRows.map\` | One \`GraphicsColumnLabelsRow\` outside row loop only |

**Mandatory grep (not fully covered by verify-layout):**

\`\`\`bash
grep -n "ticks={2}" src/components/CsatMatrixScale.tsx && echo BROKEN
grep -n "GraphicsScaleZone\\|graphicsScaleInsetPercent\\|graphicsLabelPercent" src/
grep -n "ticks={.*scaleColumns" src/components/CsatMatrixScale.tsx
grep "SliderMatrixScale" src/components/CsatMatrixScale.tsx && echo BROKEN
\`\`\`

**Docs:** [\`rating_matrix.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/rating_matrix.md) · [\`scale-position-math.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/scale-position-math.md) §RATING graphics · [\`custom-slider-track.md\`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/03-ui-specs/shared/custom-slider-track.md)
`;

function isRatingGraphicsVariant(row: MatrixVariantRow): boolean {
  return row.type === 'RATING_MATRIX' && /graphics/i.test(row.displayStyle);
}

function buildPitfallLedgerSection(variants: MatrixVariantRow[]): string {
  const sections: string[] = [];
  if (variants.some(isMatrixDropdownVariant)) sections.push(PITFALL_LEDGER_HEADER);
  if (variants.some(isBipolarSliderVariant)) sections.push(BIPOLAR_SLIDER_PITFALL_LEDGER);
  if (variants.some(isRatingGraphicsVariant)) sections.push(RATING_GRAPHICS_PITFALL_LEDGER);
  return sections.join('');
}

export function buildLayoutAuditContent(variants: MatrixVariantRow[]): string {
  if (!variants.length) {
    return `${AUDIT_HEADER}| (no matrix questions in fetch) | | | | | | | | | |\n${VISUAL_SMOKE_HEADER}| (none) | | |\n`;
  }
  const rows = variants.map(auditRowCells).join('\n');
  const smokeRows = variants.map(visualSmokeRow).join('\n');
  return `${AUDIT_HEADER}${rows}\n${VISUAL_SMOKE_HEADER}${smokeRows}\n${buildPitfallLedgerSection(variants)}`;
}

export function writeLayoutAuditFile(projectRoot: string, variants: MatrixVariantRow[]): string {
  const auditPath = path.join(projectRoot, LAYOUT_AUDIT_FILENAME);
  fs.writeFileSync(auditPath, buildLayoutAuditContent(variants), 'utf8');
  return auditPath;
}

export interface LayoutAuditParseResult {
  ok: boolean;
  errors: string[];
  brokenCount: number;
  incompleteCount: number;
}

const ALLOWED_VERDICTS = new Set(['ok', 'needs improvement']);

export function parseLayoutAuditFile(projectRoot: string): LayoutAuditParseResult {
  const auditPath = path.join(projectRoot, LAYOUT_AUDIT_FILENAME);
  const errors: string[] = [];

  if (!fs.existsSync(auditPath)) {
    return {
      ok: false,
      errors: [
        `${LAYOUT_AUDIT_FILENAME} missing — run npx cfm-sdk sync-from-fetch to generate it, then complete HOOK 3 audit rows`,
      ],
      brokenCount: 0,
      incompleteCount: 0,
    };
  }

  const content = fs.readFileSync(auditPath, 'utf8');
  const afterMatrix = content.split('## Matrix format')[1] ?? '';
  const tableSection = afterMatrix.split(/^## /m)[0] ?? afterMatrix;
  const lines = tableSection.split('\n').filter((l) => l.trim().startsWith('|'));

  let brokenCount = 0;
  let incompleteCount = 0;
  let dataRows = 0;

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    if (line.includes('Smoke test')) continue;
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no matrix questions in fetch)')) continue;

    const cells = line.split('|').map((c) => c.trim());
    const dataCells = cells.slice(1, cells.at(-1) === '' ? -1 : undefined);
    if (dataCells.length < 2) continue;

    dataRows += 1;
    const questionId = dataCells[0];
    const verdict = (dataCells[8] ?? '').trim().toLowerCase();

    if (!verdict) {
      incompleteCount += 1;
      errors.push(`${LAYOUT_AUDIT_FILENAME}: ${questionId} — Verdict column empty (set OK or Needs improvement)`);
      continue;
    }
    if (verdict === 'broken') {
      brokenCount += 1;
      errors.push(`${LAYOUT_AUDIT_FILENAME}: ${questionId} — Verdict is Broken (fix before preview)`);
      continue;
    }
    if (!ALLOWED_VERDICTS.has(verdict)) {
      incompleteCount += 1;
      errors.push(
        `${LAYOUT_AUDIT_FILENAME}: ${questionId} — Verdict must be OK or Needs improvement (got "${verdict}")`,
      );
    }
  }

  if (dataRows === 0 && !content.includes('(no matrix questions in fetch)')) {
    errors.push(`${LAYOUT_AUDIT_FILENAME}: no §3c audit rows found — fill Matrix format table`);
  }

  return {
    ok: errors.length === 0,
    errors,
    brokenCount,
    incompleteCount,
  };
}
